import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline } from "@/components/Widgets";
import { PageGuide } from "@/components/PageGuide";
import { UserCircle, FloppyDisk, UploadSimple, Trash, ImagesSquare } from "@phosphor-icons/react";

const PLATFORMS = ["Instagram", "X (Twitter)", "TikTok", "YouTube", "LinkedIn", "Facebook", "Threads"];

export default function Profile() {
  const [form, setForm] = useState({ display_name: "", handle: "", niche: "", bio: "", platforms: [], goals: "", category: "" });
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [face, setFace] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [slug, setSlug] = useState(null);
  const [customSlug, setCustomSlug] = useState("");
  const faceRef = useRef(null);
  const galleryRef = useRef(null);

  const saveSlug = async () => {
    try {
      const { data } = await api.put("/profile/slug", { slug: customSlug });
      setSlug(data.public_slug);
      toast.success("Public URL updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update URL"); }
  };
  const copyShare = () => {
    navigator.clipboard.writeText(`${window.location.origin}/api/share/profile/${slug}`);
    toast.success("Share link copied");
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      setForm({
        display_name: data.display_name || "", handle: data.handle || "", niche: data.niche || "",
        bio: data.bio || "", platforms: data.platforms || [], goals: data.goals || "", category: data.category || "",
      });
      setSlug(data.public_slug);
    }).catch(() => {});
    api.get("/branding-categories").then(({ data }) => setCats(data.categories)).catch(() => {});
    api.get("/profile/face").then(({ data }) => setFace(data.image)).catch(() => {});
    api.get("/profile/gallery").then(({ data }) => setGallery(data)).catch(() => {});
  }, []);

  const onFace = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please upload an image");
    if (f.size > 8 * 1024 * 1024) return toast.error("Image must be under 8MB");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put("/profile/face", { image: reader.result });
        setFace(reader.result);
        toast.success("Profile photo saved");
      } catch { toast.error("Upload failed"); }
    };
    reader.readAsDataURL(f);
  };

  const removeFace = async () => {
    await api.delete("/profile/face");
    setFace(null);
    toast.success("Profile photo removed");
  };

  const onGalleryFiles = (files) => {
    const list = Array.from(files || []);
    list.forEach((f) => {
      if (!f.type.startsWith("image/")) return;
      if (f.size > 8 * 1024 * 1024) return toast.error(`${f.name} is over 8MB`);
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data } = await api.post("/profile/gallery", { image: reader.result });
          setGallery((g) => [data, ...g]);
          toast.success("Photo added to gallery");
        } catch (err) { toast.error(err.response?.data?.detail || "Could not add photo"); }
      };
      reader.readAsDataURL(f);
    });
  };

  const removeGalleryPhoto = async (id) => {
    setGallery((g) => g.filter((x) => x.id !== id));
    try { await api.delete(`/profile/gallery/${id}`); } catch { toast.error("Could not remove photo"); }
  };

  const toggle = (p) => {
    setForm((f) => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p] }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/profile", form);
      if (data.public_slug) setSlug(data.public_slug);
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl fade-up space-y-6">
      <PageGuide title="How to set up your brand profile" steps={[
        "Upload your Profile Photo (big button on the left) — it shows on your public profile and keeps AI images looking like you.",
        "Add several pics to your Photo Gallery to showcase yourself on your public page.",
        "Fill in your display name, handle, niche and bio, then Save profile.",
        "Customize your public profile URL and copy the rich share link.",
      ]} />
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-1 h-fit text-center" testid="profile-avatar-card">
          <Overline className="mb-3">PROFILE PHOTO</Overline>
          <button type="button" onClick={() => faceRef.current?.click()} data-testid="avatar-upload-cta"
            className="w-full aspect-square border border-border overflow-hidden mb-3 bg-secondary flex items-center justify-center relative group">
            {face ? (
              <>
                <img src={face} alt="avatar" className="w-full h-full object-cover" data-testid="avatar-photo" />
                <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-sm font-medium gap-2">
                  <UploadSimple size={16} weight="bold" /> Change photo
                </span>
              </>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <span className="font-head font-bold text-6xl text-primary">{(form.display_name || "?").slice(0, 1).toUpperCase()}</span>
                <span className="text-xs flex items-center gap-1 text-primary"><UploadSimple size={14} weight="bold" /> Upload your photo</span>
              </div>
            )}
          </button>
          <div className="flex gap-2 mb-4">
            <button onClick={() => faceRef.current?.click()} data-testid="upload-face-btn"
              className="flex-1 bg-primary text-primary-foreground font-semibold py-2 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors duration-200">
              <UploadSimple size={15} weight="bold" /> {face ? "Change photo" : "Upload photo"}
            </button>
            {face && (
              <button onClick={removeFace} data-testid="remove-face-btn"
                className="border border-border px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:border-destructive transition-colors duration-200"><Trash size={15} weight="bold" /></button>
            )}
          </div>
          <input ref={faceRef} type="file" accept="image/*" hidden data-testid="face-file-input"
            onChange={(e) => { onFace(e.target.files[0]); e.target.value = ""; }} />
          <p className="text-[11px] text-muted-foreground mb-4 leading-relaxed">Shows as your public profile photo. Also used for face-consistent AI images across the studio.</p>

          <div className="font-head font-bold text-lg">{form.display_name || "Your brand"}</div>
          <div className="text-sm text-muted-foreground font-mono">{form.handle || "@handle"}</div>
          {slug && (
            <div className="mt-4 text-left" data-testid="public-url-editor">
              <Overline className="mb-2">PUBLIC PROFILE URL</Overline>
              <a href={`/p/${slug}`} target="_blank" rel="noreferrer" data-testid="public-profile-link"
                className="block text-xs text-primary underline break-all mb-2">/p/{slug} ↗</a>
              <div className="flex gap-2">
                <input value={customSlug} data-testid="custom-slug-input" onChange={(e) => setCustomSlug(e.target.value)}
                  placeholder="your-custom-url" className="flex-1 bg-secondary border border-border px-2 py-1.5 text-xs focus:outline-none focus:border-primary" />
                <button onClick={saveSlug} data-testid="save-slug-btn" className="border border-border px-3 py-1.5 text-xs hover:border-primary hover:text-primary transition-colors duration-200">Set</button>
              </div>
              <button onClick={copyShare} data-testid="copy-share-btn"
                className="mt-2 w-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-primary transition-colors duration-200">Copy share link (rich preview)</button>
              <p className="text-[0.65rem] text-muted-foreground mt-2 leading-relaxed">
                Custom domain? Point a CNAME to your app and contact support to map it (DNS is set at deploy).
              </p>
            </div>
          )}
        </Card>

        <Card className="md:col-span-2" testid="profile-form-card">
          <div className="flex items-center gap-2 mb-5">
            <UserCircle size={22} weight="bold" className="text-primary" />
            <h3 className="font-head font-bold text-lg">Brand Profile</h3>
          </div>
          <form onSubmit={save} className="space-y-4">
            <Input label="Display name" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} testid="profile-name" />
            <Input label="Primary handle" value={form.handle} onChange={(v) => setForm({ ...form, handle: v })} testid="profile-handle" />
            <Input label="Niche / Industry" value={form.niche} onChange={(v) => setForm({ ...form, niche: v })} testid="profile-niche" />
            <div>
              <label className="overline text-muted-foreground block mb-2">Branding category</label>
              <select value={form.category} data-testid="profile-category"
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200">
                <option value="">Select your lane…</option>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-2">Bio</label>
              <textarea value={form.bio} data-testid="profile-bio" rows={3} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200 resize-none" />
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-2">Active platforms</label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <button key={p} type="button" data-testid={`platform-${p}`} onClick={() => toggle(p)}
                    className={`text-xs font-mono px-3 py-1.5 border transition-colors duration-200 ${
                      form.platforms.includes(p) ? "border-primary bg-primary/10 text-white" : "border-border text-muted-foreground hover:text-white"
                    }`}>{p}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-2">Reputation goals</label>
              <textarea value={form.goals} data-testid="profile-goals" rows={2} onChange={(e) => setForm({ ...form, goals: e.target.value })}
                placeholder="Rebuild trust, launch product line, dominate niche keywords…"
                className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200 resize-none" />
            </div>
            <button type="submit" disabled={busy} data-testid="save-profile-btn"
              className="bg-primary text-primary-foreground font-semibold font-semibold px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
              <FloppyDisk size={16} weight="bold" /> {busy ? "Saving…" : "Save profile"}
            </button>
          </form>
        </Card>

        <Card className="md:col-span-3" testid="photo-gallery-card">
          <div className="flex items-center gap-2 mb-1">
            <ImagesSquare size={22} weight="bold" className="text-primary" />
            <h3 className="font-head font-bold text-lg">Photo Gallery</h3>
            <span className="font-mono text-sm text-muted-foreground ml-auto">{gallery.length}/12</span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Upload several personal pics — they appear in a gallery on your public profile page.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {gallery.map((g) => (
              <div key={g.id} className="relative aspect-square border border-border overflow-hidden group" data-testid={`gallery-photo-${g.id}`}>
                <img src={g.image} alt="gallery" className="w-full h-full object-cover" />
                <button onClick={() => removeGalleryPhoto(g.id)} data-testid={`gallery-delete-${g.id}`}
                  className="absolute top-1.5 right-1.5 bg-black/70 p-1.5 text-white hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity duration-200"><Trash size={14} weight="bold" /></button>
              </div>
            ))}
            {gallery.length < 12 && (
              <button onClick={() => galleryRef.current?.click()} data-testid="gallery-add-btn"
                className="aspect-square border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary transition-colors duration-200">
                <UploadSimple size={22} weight="bold" />
                <span className="text-xs">Add photos</span>
              </button>
            )}
          </div>
          <input ref={galleryRef} type="file" accept="image/*" multiple hidden data-testid="gallery-file-input"
            onChange={(e) => { onGalleryFiles(e.target.files); e.target.value = ""; }} />
        </Card>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="overline text-muted-foreground block mb-2">{label}</label>
      <input value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
    </div>
  );
}
